/**
 * Payment Service (Plug n Pay)
 *
 * Launch default is pay at session: the backend runs with
 * PAYMENTS_ENABLED=false, bookings come back with payments_enabled false,
 * and nothing here runs (contract v1 §3.4, §3.9).
 *
 * When the backend turns payments on, useBooking starts a payment only for
 * a booking whose response says payments_enabled && payment_status
 * 'pending' (the response decides, not the build-time flag). The browser
 * calls the public, token-free POST /api/public/payments/initiate
 * {booking_reference} and is sent to Plug n Pay Smart Screens by POSTing
 * the returned form fields. /api/payments/initiate is owner-only and is
 * never called from here. The backend takes the amount, email and name
 * from the booking; the client never sends them.
 * Payment status comes back via the backend callback redirect
 * (/booking-confirmation?ref=...&status=...), so there is no client-side
 * verify, refund or history call.
 */

import { post } from './apiClient';
import { safeHttpsUrl } from '../utils/safeUrl';

/**
 * Start an online payment for a booking.
 *
 * @param {string} bookingReference - booking_reference from the booking response
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{url: string, formData: object|null}>} Where to send the browser
 * @throws {Error} When the reference is missing or the response has no https URL
 * @throws {ApiError} From the API (message user-facing)
 */
export const initiatePayment = async (bookingReference, { signal } = {}) => {
  if (!bookingReference) {
    throw new Error('Booking reference is required');
  }

  const data = await post(
    '/api/public/payments/initiate',
    { booking_reference: bookingReference },
    { signal }
  );

  // https only: the browser is navigated / form-POSTed here.
  const url = safeHttpsUrl(data.smart_screens_url || data.payment_url || null);
  if (!url) {
    throw new Error('Payment page not received from server');
  }
  return {
    url,
    formData: data.form_data && typeof data.form_data === 'object' ? data.form_data : null,
  };
};

/**
 * Send the browser to the payment page. Smart Screens needs a form POST of
 * `formData`; a bare URL is followed with a normal navigation.
 *
 * @param {{url: string, formData: object|null}} payment - Result of initiatePayment
 */
export const redirectToPayment = (payment) => {
  if (!safeHttpsUrl(payment?.url)) return;

  if (!payment.formData) {
    window.location.assign(payment.url);
    return;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = payment.url;
  form.style.display = 'none';
  Object.entries(payment.formData).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value == null ? '' : String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};
